import React, { useMemo, useRef, useState, useEffect } from 'react';

import { Mic, Paperclip } from 'lucide-react';
import { toast } from 'react-toastify';

import apiClient from '../../services/apiClient';
import whisperService from '../../services/whisperService';

interface Props {
  onCreated: (projectId: string) => void;
  onCancel?: () => void;
  title?: string;
  subtitle?: string;
}

export function WorkflowInputPanel({ onCreated, onCancel, title, subtitle }: Props) {
  const [text, setText] = useState('');
  const [transcript, setTranscript] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [statusText, setStatusText] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopPromiseRef = useRef<Promise<Blob | null> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const smoothedLevelRef = useRef<number>(0);

  const pollTimerRef = useRef<number | null>(null);
  const intentIdRef = useRef<string | null>(null);

  const canSubmit = useMemo(() => {
    return Boolean(text.trim() || transcript.trim() || attachments.length > 0);
  }, [text, transcript, attachments.length]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      try {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch {}
      streamRef.current = null;
      try {
        audioCtxRef.current?.close?.();
      } catch {}
      audioCtxRef.current = null;
      analyserRef.current = null;
    };
  }, []);

  function onPickFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setAttachments((prev) => [...arr, ...prev]);
  }

  function removeAttachment(idx: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  function newIdem(prefix = 'wf') {
    try {
      return `${prefix}-${crypto.randomUUID()}`;
    } catch (_) {
      return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  }

  function pickSupportedMime() {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
    for (const c of candidates) {
      try {
        if ((window as any).MediaRecorder?.isTypeSupported?.(c)) return c;
      } catch {}
    }
    return '';
  }

  function startMeter(stream: MediaStream) {
    try {
      const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as
        | typeof AudioContext
        | undefined;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        try {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          const raw = Math.min(1, rms * 3.2);
          const prev = smoothedLevelRef.current || 0;
          const smooth = prev * 0.75 + raw * 0.25;
          smoothedLevelRef.current = smooth;
          setAudioLevel(smooth);
        } catch {}
        rafRef.current = window.requestAnimationFrame(tick);
      };
      rafRef.current = window.requestAnimationFrame(tick);
    } catch (e) {
      console.warn('Mic meter init failed:', e);
    }
  }

  async function startVoiceRecording() {
    if (isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } as any,
      });
      streamRef.current = stream;
      chunksRef.current = [];
      smoothedLevelRef.current = 0;
      setAudioLevel(0);

      const mimeType = pickSupportedMime();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) chunksRef.current.push(evt.data);
      };

      stopPromiseRef.current = new Promise<Blob | null>((resolve) => {
        recorder.onstop = () => {
          try {
            const blob = chunksRef.current.length
              ? new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
              : null;
            resolve(blob);
          } catch {
            resolve(null);
          }
        };
      });

      recorder.start(600);
      startMeter(stream);
      setIsRecording(true);
    } catch (e: any) {
      console.error('Failed to start recording:', e);
      toast.error(e?.message || 'Microphone failed to start');
      setIsRecording(false);
    }
  }

  async function stopVoiceRecording({ transcribe }: { transcribe: boolean }) {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {}
    }

    if (rafRef.current) {
      try {
        window.cancelAnimationFrame(rafRef.current);
      } catch {}
      rafRef.current = null;
    }
    setAudioLevel(0);
    smoothedLevelRef.current = 0;

    const stream = streamRef.current;
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {}
      streamRef.current = null;
    }

    const ctx = audioCtxRef.current;
    if (ctx) {
      try {
        await ctx.close();
      } catch {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;

    setIsRecording(false);

    const blob = await (stopPromiseRef.current || Promise.resolve(null));
    stopPromiseRef.current = null;
    chunksRef.current = [];

    if (!transcribe) return;
    if (!blob || blob.size === 0) {
      toast.error('No audio captured');
      return;
    }

    setIsTranscribing(true);
    try {
      // Attach audio for provenance
      try {
        const file = new File([blob], `voice-${Date.now()}.webm`, {
          type: blob.type || 'audio/webm',
        });
        setAttachments((prev) => [file, ...prev]);
      } catch {}

      const result = await whisperService.transcribeAudio(blob);
      const t = String((result as any)?.text || '').trim();
      if (!t) {
        toast.error('No transcript returned');
        return;
      }

      setText((prev) => (prev.trim() ? `${prev.trim()}\n${t}` : t));
      setTranscript((prev) => (prev ? `${prev}\n${t}` : t));
      toast.success('Transcribed');
    } catch (e: any) {
      console.error('Transcription failed:', e);
      toast.error(e?.response?.data?.message || e?.message || 'Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  }

  async function toggleRecording() {
    if (isRecording) {
      await stopVoiceRecording({ transcribe: true });
    } else {
      await startVoiceRecording();
    }
  }

  function stopPolling() {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  async function handleSubmit() {
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    setStatusText('Uploading…');
    stopPolling();

    const fd = new FormData();
    if (text.trim()) fd.append('text', text.trim());
    if (transcript.trim()) fd.append('transcript', transcript.trim());
    attachments.forEach((f) => fd.append('attachments', f));

    const idem = newIdem('workflow_intent');
    try {
      const client = apiClient.getClient();
      // Canonical V5 naming: Projects API (server mounts workflow engine at /api/projects)
      const res = await client.post('/projects/intent', fd, {
        headers: { 'X-Idempotency-Key': idem },
        withCredentials: true,
        validateStatus: (s) => (s ?? 0) < 500,
      });

      const raw = res.data;
      const intentId = raw?.data?.intentId || raw?.intentId || raw?.data?._id || raw?._id;
      if (!intentId) throw new Error(raw?.message || 'Intent creation failed');
      intentIdRef.current = String(intentId);

      setStatusText('Thinking…');

      let attempts = 0;
      const maxAttempts = 30;
      const startedAt = Date.now();
      let delayMs = 800;

      const pollOnce = async () => {
        attempts += 1;
        try {
          const st: any = await apiClient.get(`/projects/intent/${intentId}/status`);
          const payload = st?.data || {};
          const status = payload?.status;
          const projectId = payload?.projectId;
          const error = payload?.error;
          if (status === 'error') {
            setStatusText(null);
            toast.error(error || 'Workflow planning failed');
            setIsSubmitting(false);
            return;
          }
          if (projectId) {
            setStatusText(null);
            toast.success('Workflow created');
            setIsSubmitting(false);
            onCreated(String(projectId));
            return;
          }
          setStatusText(status === 'processing' ? 'Thinking…' : 'Processing…');
        } catch {}

        const elapsed = Date.now() - startedAt;
        if (attempts >= maxAttempts || elapsed >= 70_000) {
          setStatusText(null);
          toast.error('Still processing. Please ensure workflow workers are running and try again.');
          setIsSubmitting(false);
          return;
        }
        delayMs = Math.min(4500, Math.round(delayMs * 1.25));
        pollTimerRef.current = window.setTimeout(pollOnce, delayMs);
      };

      pollTimerRef.current = window.setTimeout(pollOnce, 250);
    } catch (e: any) {
      console.error('Workflow intent submit failed:', e);
      toast.error(e?.response?.data?.message || e?.message || 'Failed to create workflow');
      setStatusText(null);
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full">
      {(title || subtitle) ? (
        <div className="mb-3">
          {title ? (
            <div className="text-base font-semibold text-slate-900 dark:text-white">{title}</div>
          ) : null}
          {subtitle ? (
            <div className="mt-1 text-xs text-slate-500 dark:text-neutral-400">{subtitle}</div>
          ) : null}
        </div>
      ) : null}

      {/* Upload space */}
      <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-slate-200 bg-white p-6 text-center hover:bg-slate-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-900">
        <div className="text-sm font-semibold text-slate-900 dark:text-white">Upload</div>
        <div className="mt-1 text-sm text-slate-600 dark:text-neutral-300">
          Drop files here or click to attach. You can also use voice or paste a prompt below.
        </div>
        <input
          type="file"
          className="hidden"
          multiple
          onChange={(e) => onPickFiles(e.target.files)}
        />
      </label>

      {/* Prompt entry */}
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
        <textarea
          className="min-h-[110px] w-full resize-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-neutral-600"
          placeholder="Type your workflow / task intent… (targets, people, constraints, deadlines, budgets)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={isSubmitting}
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 hover:text-slate-900 dark:text-neutral-300 dark:hover:text-white">
              <Paperclip className="h-4 w-4" />
              <span>Attach</span>
              <input
                type="file"
                className="hidden"
                multiple
                onChange={(e) => onPickFiles(e.target.files)}
              />
            </label>

            <button
              type="button"
              onClick={() => void toggleRecording()}
              className={`flex items-center gap-2 rounded-lg px-2 py-1 text-xs ${
                isRecording
                  ? 'bg-rose-600 text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-neutral-300 dark:hover:text-white'
              }`}
              disabled={isTranscribing || isSubmitting}
            >
              <Mic className="h-4 w-4" />
              <span>{isRecording ? 'Stop' : 'Voice'}</span>
            </button>

            {/* Mic meter */}
            <div className="flex items-center gap-1">
              {Array.from({ length: 10 }).map((_, i) => {
                const level = Math.max(0, Math.min(1, audioLevel));
                const active = i / 10 < level;
                return (
                  <div
                    key={i}
                    className={`h-2 w-1 rounded-sm ${
                      active ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-neutral-800'
                    }`}
                  />
                );
              })}
            </div>

            {isTranscribing ? (
              <span className="text-xs text-slate-500 dark:text-neutral-400">Transcribing…</span>
            ) : null}
            {statusText ? (
              <span className="text-xs text-slate-500 dark:text-neutral-400">{statusText}</span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {onCancel ? (
              <button
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-900"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            ) : null}
            <button
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? 'Creating…' : 'Create workflow'}
            </button>
          </div>
        </div>

        {attachments.length ? (
          <div className="mt-3 space-y-2">
            {attachments.slice(0, 8).map((f, idx) => (
              <div
                key={`${f.name}-${idx}`}
                className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700 dark:bg-neutral-900 dark:text-neutral-200"
              >
                <div className="min-w-0 truncate">{f.name}</div>
                <button
                  className="ml-3 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-800"
                  onClick={() => removeAttachment(idx)}
                  disabled={isSubmitting}
                >
                  Remove
                </button>
              </div>
            ))}
            {attachments.length > 8 ? (
              <div className="text-[11px] text-slate-500 dark:text-neutral-400">
                +{attachments.length - 8} more…
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

